import { pxToChars } from "../../../utils/excelToFortuneSheet";
import { COLUMN_TYPE_TO_WIDTH, DEFAULT_EMPTY_COLUMN_WIDTH, DEFAULT_NOTES_COLUMN_WIDTH } from "../constants";
import type { PrintoutColumn } from "../types";

interface ComputeColumnWidthsArgs {
	enabledColumns: PrintoutColumn[];
	printNotes: boolean;
	/** Blank columns appended after the last real one (see `RenderSettings.appendEmptyColumns`). */
	appendEmptyColumns: number;
	columnWidthOverrides: Record<string, number> | undefined;
}

/** A width override (px) for the sheet column at this 0-based index, else the column's own default (chars). */
const widthAt = (index: number, defaultChars: number, columnWidthOverrides: Record<string, number> | undefined): { width: number } => {
	const overridePx = columnWidthOverrides?.[String(index)];
	return { width: overridePx ? pxToChars(overridePx) : defaultChars };
};

/**
 * Per-column widths for a worksheet (mapping §8.3). Preview overrides arrive in pixels and are
 * converted with `pxToChars` — the exact inverse of the conversion the preview was built with, so a
 * dragged column comes back the width it was dragged to.
 *
 * With `printNotes` on and no `notes` column already in the list, a trailing one is added. The
 * `appendEmptyColumns` blank columns come last, after that Notes column. Overrides are keyed by the
 * 0-based sheet column index — the same key the preview's drag reports — so a dragged blank column
 * keeps its width exactly like a real one.
 */
export const computeColumnWidths = ({ enabledColumns, printNotes, appendEmptyColumns, columnWidthOverrides }: ComputeColumnWidthsArgs): { width: number }[] => {
	const widths = enabledColumns.map((col, index) => widthAt(index, COLUMN_TYPE_TO_WIDTH[col.type], columnWidthOverrides));
	if (printNotes) {
		const hasNotesColumn = enabledColumns.some((col) => col.type === "notes");
		if (!hasNotesColumn) widths.push(widthAt(widths.length, DEFAULT_NOTES_COLUMN_WIDTH, columnWidthOverrides));
	}
	for (let blank = 0; blank < appendEmptyColumns; blank++) {
		widths.push(widthAt(widths.length, DEFAULT_EMPTY_COLUMN_WIDTH, columnWidthOverrides));
	}
	return widths;
};
